import { useState } from "react";
import ContentLayout from "../components/layout/ContentLayout";
import {
  ColorPicker,
  Form,
  Input,
  message,
  Switch,
  Button,
  Tooltip,
  Image,
} from "antd";
import RewardForm from "../components/RewardForm";
import { useNavigate, useParams } from "react-router-dom";
import FlipCardLocal from "../components/FlipCardLocal";
import {
  MoonOutlined,
  QuestionCircleOutlined,
  SunOutlined,
} from "@ant-design/icons";
import StampImageUploader from "../components/StampImageUploader";
import { createStampCard } from "../actions/acc_actions";
import IconSelector from "./IconSelector";
import ErrorBanner from "./ErrorBanner";

const layouts = [
  {
    id: "layout1",
    label: "Layout 1",
    img: "/assets/images/stampcard/card-grid-2.png", // use your actual image path
  },
  {
    id: "layout2",
    label: "Layout 2",
    img: "/assets/images/stampcard/card-grid-3.png", // use your actual image path
  },
];

const getFirstRewardValue = (layout, hasWelcome) => {
  if (layout === "layout1") return 5;
  if (layout === "layout2") return 8;
};

export default function StampCardFrom() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { accUrn, businessSlug } = useParams();
  const name = businessSlug;

  const [errors, setErrors] = useState();
  const [rewardsAssets, setRewardsAssets] = useState([]);
  const [cardFlipped, setCardFlipped] = useState(true);
  const [isLoading, setIsLoading] = useState();
  const [selectedLayout, setSelectedLayout] = useState("layout1");
  const [hasWelcomeReward, setHasWelcomeReward] = useState(false);
  const [stampLogo, setstampLogo] = useState(null);
  const [stampBackground, setstampBackground] = useState(null);
  const [welcomeRewardAssests, setWelcomeRewardAssets] = useState({
    inkingIcon: null,
    targetIcon: null,
  });
  const [defaultAssets, setDefaultAssets] = useState({
    inkingIcon: null,
    targetIcon: null,
  });
  const [showEncouragementMessaging, setShowEncouragementMessaging] =
    useState(false);
  const [card, setCard] = useState({
    terms: null,
    encourageMessage: null,
    extraInfo: null,
    colors: {
      textFront: null,
      textBack: null,
      bgFront: null,
      bgBack: null,
      pagerColor: "#000000",
      ambienceColor: "#000000",
      infoColor: "#000000",
      infoTintColor: "#000000",
      voucherThumbnailColor: "#000000",
    },
  });
  const [theme, setTheme] = useState("light"); // default theme

  const toggleTheme = (checked) => {
    const newTheme = checked ? "dark" : "light";
    setTheme(newTheme);
  };

  const handleValueChange = () => {
    const rewards = form.getFieldsValue()?.rewards || [];
    setRewardsAssets(rewards);

    const firstValue = getFirstRewardValue(selectedLayout, hasWelcomeReward);

    const updated = [...(rewards.length ? rewards : [{}])];
    updated[0] = {
      ...updated[0],
      qualifyingPurchases: firstValue,
    };

    form.setFieldsValue({ rewards: updated });

    const used = new Set();
    used.add(firstValue);
    for (let i = 1; i < updated.length; i++) {
      const val = updated[i]?.qualifyingPurchases;
      if (val !== undefined && val !== null) {
        used.add(val);
        used.add(val - 1);
        used.add(val + 1);
      }
    }
  };

  const flipCard = () => {
    setCardFlipped(!cardFlipped);
  };

  const processRewards = (rewards) => {
    // Map rewards to include icon and handle title
    let editedRewards = rewards.map((reward) =>
      reward.title
        ? {
            // copy all props except targetIcon and inkingIcon
            ...(({ targetIcon, inkingIcon, ...rest }) => rest)(reward),

            // add/override icon and assets
            icon: reward.title,
            assets: {
              target: reward.targetIcon?.fileName,
              inking: reward.inkingIcon?.fileName,
              confirmation: reward.inkingIcon?.fileName,
            },
          }
        : reward
    );

    // If there are exactly 2 rewards, decrease the highest qualifyingPurchases by 1
    if (editedRewards.length === 2) {
      // Find the reward with the highest qualifyingPurchases
      const maxRewardIndex = editedRewards.reduce(
        (maxIndex, reward, index, arr) =>
          reward.qualifyingPurchases > arr[maxIndex].qualifyingPurchases
            ? index
            : maxIndex,
        0
      );

      // Decrease qualifyingPurchases by 1 for the highest reward
      editedRewards = editedRewards.map((reward, index) =>
        index === maxRewardIndex
          ? { ...reward, qualifyingPurchases: reward.qualifyingPurchases - 1 }
          : reward
      );
    }

    return editedRewards;
  };

  const onFinish = (values) => {
    const {
      cardFrontColor,
      cardFrontTextColor,
      cardBackColor,
      cardBackTextColor,
      pagerColor,
      ambienceColor,
      infoColor,
      infoTintColor,
      voucherThumbnailColor,
      welcomReward_confirmationTitle,
      welcomReward_description,
      welcomReward_icon,
      welcomeReward_confirmationMessage,
      welcomeReward_terms,
      welcomeReward_title,
      rewards,
      ...restValues
    } = values;

    const finalData = {
      ...restValues,
      fromIconLibrary: true,
      cardName: name + Math.floor(new Date().getTime() / 1000),
      colors: {
        cardFrontColor: cardFrontColor.toHexString(),
        cardFrontTextColor: cardFrontTextColor.toHexString(),
        cardBackColor: cardBackColor.toHexString(),
        cardBackTextColor: cardBackTextColor.toHexString(),
        pagerColor: card.colors.pagerColor,
        ambienceColor: card.colors.ambienceColor,
        infoColor: card.colors.infoColor,
        infoTintColor: card.colors.infoTintColor,
        voucherThumbnailColor: card.colors.voucherThumbnailColor,
      },

      hasWelcomeReward,
      ...(hasWelcomeReward && {
        welcomeReward: {
          confirmationTitle: welcomReward_confirmationTitle,
          description: welcomReward_description,
          icon: welcomeReward_title,
          confirmationMessage: welcomeReward_confirmationMessage,
          terms: welcomeReward_terms,
          title: welcomeReward_title,
          assets: {
            target: welcomeRewardAssests.targetIcon?.fileName,
            inking: welcomeRewardAssests.inkingIcon?.fileName,
            confirmation: welcomeRewardAssests.inkingIcon?.fileName,
          },
        },
      }),
      logoPath: stampLogo?.fileName,
      backgroundPath: stampBackground?.fileName,
      defaultAssets: {
        target: defaultAssets.targetIcon?.fileName,
        inking: defaultAssets.inkingIcon?.fileName,
        confirmation: defaultAssets.inkingIcon?.fileName,
      },
      rewards: processRewards(rewards),
      showEncouragementMessaging,
      theme: theme,
      businessSlug,
    };

    setIsLoading(true);
    createStampCard(accUrn, finalData)
      .then(() => {
        setTimeout(() => {
          setIsLoading(false);
          navigate("/stamp-card");
        }, 5000);
        message.success("Stampcard created successfully");
      })
      .catch((err) => {
        const validationErrors =
          err.response?.data?.body || err.response?.data?.error?.errors || [];

        if (validationErrors.length > 0) {
          const errorMessages = validationErrors.map((e) => e.msg);
          setErrors(errorMessages); // Show in banner
        } else {
          setErrors([err?.response?.data?.message || "Something went wrong!"]);
        }

        setIsLoading(false);
      });
  };

  return (
    <ContentLayout
      className="stampcard"
      header={
        <>
          <h1>
            Welcome to <br /> <span>{name}</span>
          </h1>
          <p className="content_layout-subtitle">
            Now you can design and customize your Stamp Card layout to reflect
            your brand, reward your customers, and encourage repeat visits.
            Choose from multiple layouts and start creating a memorable
            experience for your customers today!
          </p>
        </>
      }
    >
      <Form
        form={form}
        className="rewards-form"
        onFinish={onFinish}
        layout="vertical"
        onValuesChange={handleValueChange}
        initialValues={{
          rewards: [
            {
              qualifyingPurchases: getFirstRewardValue(
                selectedLayout,
                hasWelcomeReward
              ),
            },
          ],
        }}
      >
        <>
          <div className="rewards-form-section">
            <div className="rewards-form-title">
              <h4> Layout</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="layout-selector-wrapper">
              {layouts.map((layout) => (
                <div
                  key={layout.id}
                  className={`layout-box ${
                    selectedLayout === layout.id ? "selected" : "faded"
                  }`}
                  onClick={() => setSelectedLayout(layout.id)}
                >
                  <img src={layout.img} alt={layout.label} />
                  <div className="layout-label">{layout.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rewards-form-section">
            <div className="rewards-form-title">
              <h4>Colors</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="rewards-inputs">
              <div className="form-row">
                <Form.Item
                  name="cardFrontColor"
                  label="Card Front Color"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <ColorPicker
                    style={{ width: "100%" }}
                    showText
                    onChange={(color) =>
                      setCard((prev) => ({
                        ...prev,
                        colors: {
                          ...prev.colors,
                          bgFront: color.toHexString(), // or any value you want to set
                        },
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="cardFrontTextColor"
                  label="Card Front Text Color"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <ColorPicker
                    onChange={(color) =>
                      setCard((prev) => ({
                        ...prev,
                        colors: {
                          ...prev.colors,
                          textFront: color.toHexString(), // or any value you want to set
                        },
                      }))
                    }
                    style={{ width: "100%" }}
                    showText
                  />
                </Form.Item>
              </div>
              <div className="form-row">
                <Form.Item
                  name="cardBackColor"
                  label="Card Back Color"
                  rules={[
                    {
                      required: true,
                      message: "Card Back Color is required",
                    },
                  ]}
                >
                  <ColorPicker
                    onChange={(color) =>
                      setCard((prev) => ({
                        ...prev,
                        colors: {
                          ...prev.colors,
                          bgBack: color.toHexString(), // or any value you want to set
                        },
                      }))
                    }
                    style={{ width: "100%" }}
                    showText
                  />
                </Form.Item>
                <Form.Item
                  name="cardBackTextColor"
                  label="Card Back Text Color"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <ColorPicker
                    onChange={(color) =>
                      setCard((prev) => ({
                        ...prev,
                        colors: {
                          ...prev.colors,
                          textBack: color.toHexString(), // or any value you want to set
                        },
                      }))
                    }
                    style={{ width: "100%" }}
                    showText
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          <Form.Item className="rewards-form-section">
            <div className="rewards-form-title">
              <h4> Logo & Background</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <StampImageUploader label="Logo" onChange={setstampLogo} />
            {/* <StampImageUploader
              label="Background"
              onChange={setstampBackground}
            /> */}
          </Form.Item>

          <Form.Item className="rewards-form-section">
            <div className="rewards-form-title">
              <h4> default icons</h4>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  padding: "12px 24px",
                  borderRadius: 12,
                  background: theme === "dark" ? "#1f1f1f" : "#fafafa",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  width: 220,
                  margin: "40px auto",
                  transition: "background 0.3s ease",
                }}
              >
                <SunOutlined
                  style={{ color: theme === "light" ? "#faad14" : "#999" }}
                />
                <Switch
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  checkedChildren="Dark"
                  unCheckedChildren="Light"
                  style={{
                    backgroundColor: theme === "dark" ? "#722ed1" : "#1890ff",
                  }}
                />
                <MoonOutlined
                  style={{ color: theme === "dark" ? "#722ed1" : "#999" }}
                />
              </div>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <IconSelector
              accUrn={accUrn}
              businessSlug={businessSlug}
              theme={theme}
              onChange={({ target, inking }) => {
                setDefaultAssets({
                  inkingIcon: inking,
                  targetIcon: target,
                });
              }}
            />
          </Form.Item>

          <div className="rewards-form-section">
            <div className="rewards-form-title">
              <h4> Welcome Reward</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="rewards-inputs">
              <Form.Item
                label={<h4>Has Welcome Reward?</h4>}
                layout="horizontal"
              >
                <Switch
                  checked={hasWelcomeReward}
                  onChange={() => setHasWelcomeReward(!hasWelcomeReward)}
                />
              </Form.Item>
              {hasWelcomeReward && (
                <>
                  <IconSelector
                    accUrn={accUrn}
                    businessSlug={businessSlug}
                    theme={theme}
                    onChange={({ target, inking }) => {
                      setWelcomeRewardAssets({
                        inkingIcon: inking,
                        targetIcon: target,
                      });
                    }}
                  />
                  <div className="form-row">
                    <Form.Item
                      name="welcomeReward_title"
                      label="Title"
                      rules={[{ required: true, message: "Title is required" }]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="form-row">
                    <Form.Item
                      name="welcomReward_confirmationTitle"
                      label="Confirmation Title"
                      rules={[
                        {
                          required: true,
                          message: "Confirmation Title is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="welcomeReward_confirmationMessage"
                      label="Confirmation Message"
                      rules={[
                        {
                          required: true,
                          message: "Confirmation Message is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="form-row">
                    <Form.Item
                      name="welcomReward_description"
                      label="Description"
                      rules={[
                        {
                          required: true,
                          message: "Description is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="welcomeReward_terms"
                      label="Terms"
                      rules={[
                        { required: true, message: "Terms are required" },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                </>
              )}
            </div>
          </div>

          <Form.Item name="rewards">
            <RewardForm
              form={form}
              selectedLayout={selectedLayout}
              hasWelcomeReward={hasWelcomeReward}
              setRewardsAssets={setRewardsAssets}
            />
          </Form.Item>

          <div className="rewards-form-section">
            <div className="rewards-form-title">
              <h4> Appointments</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="rewards-inputs">
              <Form.Item
                label={<h4>Show Encouragement Messaging?</h4>}
                layout="horizontal"
              >
                <Switch
                  checked={showEncouragementMessaging}
                  onChange={() =>
                    setShowEncouragementMessaging(!showEncouragementMessaging)
                  }
                />
              </Form.Item>

              {showEncouragementMessaging && (
                <Form.Item
                  name="encouragementMessageRaw"
                  label="Encouragement Message"
                  rules={[
                    {
                      required: true,
                      message: "Encouragement Message is required",
                    },
                  ]}
                >
                  <Input
                    onChange={(e) =>
                      setCard((card) => ({
                        ...card,
                        encourageMessage: e.target.value,
                      }))
                    }
                  />
                </Form.Item>
              )}
            </div>
          </div>
          <div className="rewards-form-section">
            <div className="rewards-form-title">
              <h4>Extra Info.</h4>
              <Tooltip
                title={
                  <Image
                    src="/assets/images/stampcard/stamp-hint.png"
                    alt="Hint"
                    style={{ maxWidth: 300 }}
                  />
                }
              >
                <QuestionCircleOutlined className="tooltip-icon" />
              </Tooltip>
            </div>
            <div className="rewards-inputs">
              <Form.Item name="termsText" label="Terms Text" required>
                <Input
                  onChange={(e) =>
                    setCard((card) => ({ ...card, terms: e.target.value }))
                  }
                />
              </Form.Item>
              {/* <Form.Item name="extraInfo" label="Extra Info." required>
                <Input
                  onChange={(e) =>
                    setCard((card) => ({
                      ...card,
                      extraInfo: e.target.value,
                    }))
                  }
                />
              </Form.Item> */}
            </div>
          </div>
        </>

        <div className="mockup-container">
          <FlipCardLocal
            flipCard={flipCard}
            flipped={cardFlipped}
            className="mockup-card"
            card={card}
            logo={stampLogo}
            background={stampBackground}
            layout={selectedLayout}
            defaultAssets={defaultAssets}
            welcomeRewardAssests={welcomeRewardAssests}
            rewardsAssets={rewardsAssets}
            showEncourageMsg={showEncouragementMessaging}
          />
          <img
            className="mockup-frame"
            src="/assets/images/stampcard/mobile.png"
            alt=""
          />

          <h5 className="active">{cardFlipped ? "Card Back" : "Card Front"}</h5>

          <Button
            style={{ padding: "1.5rem 8rem", fontWeight: 500 }}
            htmlType="submit"
            type="primary"
            loading={isLoading}
          >
            Submit
          </Button>
        </div>
      </Form>
      <ErrorBanner errors={errors} onClose={() => setErrors([])} />
    </ContentLayout>
  );
}
